type TemplateData = {
  retailerName?: string;
  tenderName?: string;
  contactName?: string;
  organizationName?: string;
  sites?: any[]; // each site may include dueAt (string | Date)
  tenderNotes?: string;
  // Step-1 tender details
  entityName?: string;
  abn?: string;
  acn?: string;
  startDate?: string;
  contractTerms?: string;
  comms?: string;
  dma?: string;
  address?: string;
  pricingDueDate?: string;
  specialRequests?: string;
  // Solar site details
  solarCapacity?: string;
  estimatedGeneration?: string;
  estimatedFeedIn?: string;
};

const fmtNumber = (val: number | string | undefined | null): string => {
  if (val === undefined || val === null || val === '') return '';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return String(val);
  return n.toLocaleString('en-AU');
};

const fmtDate = (val: string | Date | undefined | null): string => {
  if (!val) return '';
  try {
    const d = typeof val === 'string' ? new Date(val) : val;
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(val);
  }
};

const addOneDay = (val: string | Date | undefined | null): Date | null => {
  if (!val) return null;
  try {
    const d = typeof val === 'string' ? new Date(val) : new Date(val.getTime());
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 1);
    return d;
  } catch {
    return null;
  }
};

export const replacePlaceholders = (content: string, data: TemplateData): string => {
  let s = content;

  // Existing placeholders
  s = s.replace(/{{Retailer Name}}/g,     data.retailerName     || '{{Retailer Name}}');
  s = s.replace(/{{Tender Name}}/g,       data.tenderName       || 'Tender');
  s = s.replace(/{{Contact Name}}/g,      data.contactName      || 'Team');
  s = s.replace(/{{Organization Name}}/g, data.organizationName || 'Choice Energy');

  // Resolve first site up front so it's available for all placeholders below
  const firstSite = data.sites?.[0];

  // Step-1 client / tender detail placeholders
  s = s.replace(/{{Entity Name}}/g,       firstSite?.name || data.entityName || '');
  s = s.replace(/{{ABN}}/g,               data.abn                          || '');
  s = s.replace(/{{ACN}}/g,               firstSite?.acn || data.acn        || '');
  s = s.replace(/{{Start Date}}/g,        fmtDate(data.startDate)           || '');
  s = s.replace(/{{Contract Terms}}/g,    data.contractTerms                || '');
  s = s.replace(/{{Comms}}/g,             data.comms                        || '');
  s = s.replace(/{{DMA}}/g,              data.dma                          || '');
  s = s.replace(/{{Address}}/g,           data.address                      || '');
  s = s.replace(/{{Pricing Due Date}}/g, fmtDate(firstSite?.dueAt || data.pricingDueDate) || '');
  s = s.replace(/{{Special Requests}}/g,  data.specialRequests              || '');
  s = s.replace(/{{Number of Sites}}/g,   String(data.sites?.length ?? 0));

  // Solar / feed-in details (kept for backwards-compat with older templates)
  s = s.replace(/{{Solar Capacity}}/g,       data.solarCapacity       || '');
  s = s.replace(/{{Estimated Generation}}/g, data.estimatedGeneration || '');
  s = s.replace(/{{Estimated Feed In}}/g,    data.estimatedFeedIn     || '');

  // Usage — pulled from the first site in `data.sites`
  s = s.replace(/{{siteName}}/g,      firstSite?.name                              || '');
  s = s.replace(/{{NMI}}/g,          firstSite?.nmi                               || '');
  s = s.replace(/{{Peak kWh}}/g,     fmtNumber(firstSite?.annualKwhPeak)          || '');
  s = s.replace(/{{Off Peak kWh}}/g, fmtNumber(firstSite?.annualKwhOffpeak)       || '');
  s = s.replace(/{{Shoulder kWh}}/g, fmtNumber(firstSite?.annualKwhShoulder ?? 0) || '');
  const totalKwh = firstSite
    ? (firstSite.annualKwhTotal ?? ((firstSite.annualKwhPeak || 0) + (firstSite.annualKwhOffpeak || 0) + (firstSite.annualKwhShoulder || 0)))
    : undefined;
  s = s.replace(/{{Total kWh}}/g,    fmtNumber(totalKwh) || '');

  // Sentinel prevents double-injection when replacePlaceholders is called
  // a second time on an already-processed body (e.g. on editor refresh or send).
  const SITE_SENTINEL = '<!--ce-sites-->';

  // Site details table — replace placeholder or auto-inject when multi-site
  const generatedSiteTable = generateSiteTable(data.sites || [], {
    dma:             data.dma,
    specialRequests: data.specialRequests,
    contractTerms:   data.contractTerms,
    abn:             data.abn,   // fallback when site.abn is missing/wrong
    acn:             data.acn,   // fallback when site.acn is missing
  });
  if (s.includes('{{Site Details Table}}')) {
    s = s.replace(/{{Site Details Table}}/g, SITE_SENTINEL + generatedSiteTable);
  } else if (
    !s.includes(SITE_SENTINEL) &&
    (data.sites?.length ?? 0) > 1 &&
    (s.includes('<table') || s.includes('<body') || s.includes('<div'))
  ) {
    // Template predates the placeholder — inject the full table INSIDE the
    // email card (before the last closing </td> which ends the content cell).
    // Guard: only run on HTML bodies, never on plain-text subject lines.
    const injectedTable = [
      SITE_SENTINEL,
      '<div style="margin-top:20px;padding-top:14px;border-top:1px solid #e5e9f0;">',
      `<p style="font-family:'Segoe UI',Arial,sans-serif;font-size:10px;font-weight:800;`,
      `letter-spacing:1.5px;text-transform:uppercase;color:#3790c5;margin:0 0 8px 0;">`,
      `All Sites (${data.sites!.length}):`,
      '</p>',
      generatedSiteTable,
      '</div>',
    ].join('');

    // Injection priority:
    // 1. Before last </td> (inside the main content cell)
    // 2. Before last </table> (inside the outer wrapper)
    // 3. Before </body>
    // 4. Append at end
    const lastTdIdx = s.lastIndexOf('</td>');
    const lastTableIdx = s.lastIndexOf('</table>');
    if (lastTdIdx !== -1) {
      s = s.substring(0, lastTdIdx) + injectedTable + s.substring(lastTdIdx);
    } else if (lastTableIdx !== -1) {
      s = s.substring(0, lastTableIdx) + injectedTable + s.substring(lastTableIdx);
    } else if (/<\/body>/i.test(s)) {
      s = s.replace(/<\/body>/i, injectedTable + '</body>');
    } else {
      s += '\n' + injectedTable;
    }
  }

  return s;
};

export const generateSiteTable = (
  sites: any[],
  common?: { dma?: string; specialRequests?: string; contractTerms?: string; abn?: string; acn?: string },
): string => {
  if (!sites || sites.length === 0) return '';

  // Columns: Site Name | NMI | ABN | ACN | Address | Comms | Term | Start Date | Solar Info
  //          | Peak kWh | Shoulder kWh | Off-Peak kWh | Total kWh
  // Site-level ABN/ACN shown here; falls back to client-level from common if site value is empty.
  const B    = 'border:1px solid #e2e8f0;';
  const TH   = `${B}padding:10px 14px;text-align:left;font-size:10px;font-weight:800;` +
               `letter-spacing:1.2px;text-transform:uppercase;color:#3790c5;background:#f0f7ff;white-space:nowrap;`;
  const TH_R = TH.replace('text-align:left', 'text-align:right');

  let html = `<div style="overflow-x:auto;width:100%;">
  <table cellpadding="0" cellspacing="0" border="0" width="100%"
    style="border-collapse:collapse;min-width:1150px;width:100%;border:1px solid #e2e8f0;
           border-radius:10px;overflow:hidden;font-family:'Segoe UI',Arial,sans-serif;
           font-size:13px;margin-bottom:8px;">
    <thead>
      <tr>
        <th style="${TH}">Site Name</th>
        <th style="${TH}">NMI</th>
        <th style="${TH}">ABN</th>
        <th style="${TH}">ACN</th>
        <th style="${TH}">Address</th>
        <th style="${TH}">Comms</th>
        <th style="${TH}">Term</th>
        <th style="${TH}">Start Date</th>
        <th style="${TH}">Solar Info</th>
        <th style="${TH_R}">Peak kWh</th>
        <th style="${TH_R}">Shoulder kWh</th>
        <th style="${TH_R}">Off-Peak kWh</th>
        <th style="${TH_R}">Total kWh</th>
      </tr>
    </thead>
    <tbody>`;

  sites.forEach((site, i) => {
    const bg    = i % 2 === 0 ? '#ffffff' : '#f8fafc';
    const addr  = [site.address?.street, site.address?.city,
                   site.address?.state,  site.address?.postcode]
                   .filter(Boolean).join(', ');
    const total = site.annualKwhTotal
      ?? ((site.annualKwhPeak || 0) + (site.annualKwhOffpeak || 0) + (site.annualKwhShoulder || 0));
    const startDate = fmtDate(site.startDate);
    const TD      = `${B}padding:10px 14px;color:#0f172a;background:${bg};`;
    const TD_R    = `${TD}text-align:right;`;
    const TD_MONO = `${TD}font-family:monospace;letter-spacing:0.5px;`;
    const isLast  = i === sites.length - 1;
    const bb      = isLast ? '' : 'border-bottom:1px solid #e2e8f0;';

    const solarParts = [
      site.solarCapacity       ? `Cap: ${site.solarCapacity}`       : '',
      site.estimatedGeneration ? `Gen: ${site.estimatedGeneration}` : '',
      site.estimatedFeedIn     ? `FIT: ${site.estimatedFeedIn}`     : '',
    ].filter(Boolean);
    const solarInfo = solarParts.length > 0 ? solarParts.join('<br>') : '—';

    html += `
      <tr>
        <td style="${TD}font-weight:600;${bb}">${site.name || '—'}</td>
        <td style="${TD_MONO}${bb}">${site.nmi || '—'}</td>
        <td style="${TD_MONO}${bb}">${site.abn || common?.abn || '—'}</td>
        <td style="${TD_MONO}${bb}">${site.acn || common?.acn || '—'}</td>
        <td style="${TD}color:#374151;${bb}">${addr || '—'}</td>
        <td style="${TD}${bb}">${site.retailerComms || '—'}</td>
        <td style="${TD}${bb}">${common?.contractTerms || '—'}</td>
        <td style="${TD}white-space:nowrap;${bb}">${startDate || '—'}</td>
        <td style="${TD}font-size:12px;line-height:1.6;${bb}">${solarInfo}</td>
        <td style="${TD_R}${bb}">${fmtNumber(site.annualKwhPeak)     || '—'}</td>
        <td style="${TD_R}${bb}">${fmtNumber(site.annualKwhShoulder) || '—'}</td>
        <td style="${TD_R}${bb}">${fmtNumber(site.annualKwhOffpeak)  || '—'}</td>
        <td style="${TD_R}font-weight:700;${bb}">${fmtNumber(total)  || '—'}</td>
      </tr>`;
  });

  html += `
    </tbody>
  </table>
</div>`;

  return html;
};
